import { Clipboard } from '@angular/cdk/clipboard';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IS_MOBILE } from '@core/misc/misc.utils';
import { environment } from '@env/environment';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { SelectorComponent } from '@shared/components/selector/selector.component';
import { format } from 'date-fns';
import { map } from 'rxjs';
import { TypedTemplateDirective } from '../../../shared/directives/typed-template.directive';
import { SecondsCounterPipe } from '../../../shared/pipes/seconds-counter.pipe';
import { UserState } from '../user/state/user.state';
import { ResponseLogItem } from './response-logger.type';
import { ResponseLoggerState } from './state/response-logger.state';

@Component({
  selector: 'app-response-logger',
  templateUrl: './response-logger.component.html',
  styleUrl: './response-logger.component.scss',
  imports: [
    TypedTemplateDirective,
    NgClass,
    MatIcon,
    SelectorComponent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    TranslatePipe,
    SecondsCounterPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResponseLoggerComponent {
  readonly #store = inject(Store);
  readonly #clipboard = inject(Clipboard);
  readonly #snackBar = inject(MatSnackBar);
  readonly #translateService = inject(TranslateService);

  readonly isMobile = IS_MOBILE;
  logs$ = this.#store
    .select<ResponseLogItem[]>(ResponseLoggerState.responsesLog)
    .pipe(map((log) => log.map((l) => (l.type === 'progress' ? { ...l, disabled: true } : l))));
  logsTypedToken!: ResponseLogItem;

  selectedLogsData = new FormControl<string | null>(null);
  readonly #userData = this.#store.selectSnapshot(UserState.userData);

  onCopy() {
    if (this.selectedLogsData.value) {
      this.#clipboard.copy(this.selectedLogsData.value);
      this.#snackBar.open(this.#translateService.instant('Data of selected logs saved to clipboard'), '', {
        panelClass: 'snackbar-success',
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    }
  }

  onLogSelect(logs: ResponseLogItem[] | null) {
    if (!logs) return;
    if (logs?.length) {
      const logContent: { log: string; type: string; data: unknown }[] = [];
      logs
        .filter((e) => e.type !== 'progress')
        .forEach((e) => {
          logContent.push({
            log: `${e.pageTitle}:${e.apiUrl}:${format(e.requestAt, environment.formats.dateTime)} - ${format(e.responseAt as Date, environment.formats.dateTime)} (${e.duration})`,
            type: e.type,
            data: e
          });
        });

      logContent.push({ log: 'User Data', type: 'info', data: this.#userData });

      let connection = (navigator as { connection?: Record<string, unknown> })?.connection;
      if (connection) {
        const { downlink, effectiveType, rtt, saveData } = connection;
        connection = { downlink, effectiveType, rtt, saveData };
      }
      logContent.push({
        log: 'Browsing Data',
        type: 'info',
        data: {
          userAgent: navigator.userAgent,
          languages: navigator.languages,
          dateTimeFormatOptions: Intl.DateTimeFormat().resolvedOptions(),
          connection
        }
      });

      this.selectedLogsData.setValue(JSON.stringify(logContent));
    } else this.selectedLogsData.setValue(null);
  }
}
